// src/pages/DiaryCalendar.js — 일기 달력 페이지 (/diary?month=YYYY-MM)
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import AppHeader from '../components/AppHeader';
import { formatLocalDate } from '../utils/storageUtils';
import { apiFetch } from '../utils/apiFetch';
import '../styles/DiaryCalendar.css';

const DiaryCalendar = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [diaries, setDiaries] = useState({});
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState('');

    // URL이 진실 공급원 — ?month=YYYY-MM이 없거나 형식이 틀리면 이번 달
    const parseMonthParam = (value) => {
        if (value && /^\d{4}-\d{2}$/.test(value)) {
            const [y, m] = value.split('-').map(Number);
            if (m >= 1 && m <= 12) return new Date(y, m - 1, 1);
        }
        return new Date();
    };
    const currentDate = parseMonthParam(searchParams.get('month'));
    // Date 객체는 렌더마다 새로 생기므로, 조회 의존성은 문자열 키로 고정한다
    const monthKey = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;

    // 라우트 가드 — 토큰 없이 직접 URL 진입 시 로그인으로
    useEffect(() => {
        if (!localStorage.getItem('accessToken')) {
            navigate('/login');
        }
    }, [navigate]);

    const fetchMonthlyDiaries = useCallback(async () => {
        setIsLoading(true);
        setError('');

        try {
            const [year, month] = monthKey.split('-');

            const response = await apiFetch(`/diaries/monthly?year=${year}&month=${month}`, {
                method: 'GET'
            });

            if (response.ok) {
                const result = await response.json();

                // 백엔드 응답 구조에 맞게 수정: result.data.diaryList
                const diaryMap = {};
                if (result.data && result.data.diaryList && Array.isArray(result.data.diaryList)) {
                    result.data.diaryList.forEach(diary => {
                        const dateKey = diary.date; // "date" 필드 사용
                        diaryMap[dateKey] = {
                            diaryId: diary.diaryId,
                            title: diary.title,
                            date: diary.date
                        };
                    });
                }

                setDiaries(diaryMap);
            } else {
                const errorData = await response.json();
                setError(errorData.errorMessage || errorData.message || '일기 조회에 실패했습니다.');
            }
        } catch (error) {
            console.error('일기 조회 오류:', error);
            setError('네트워크 오류가 발생했습니다.');
        } finally {
            setIsLoading(false);
        }
    }, [monthKey]);

    useEffect(() => {
        fetchMonthlyDiaries();
    }, [fetchMonthlyDiaries]);

    // 월 이동 — 쿼리를 push로 갱신해 뒤로가기가 "이전에 보던 달"이 되게 한다
    const goToMonth = (delta) => {
        const next = new Date(currentDate.getFullYear(), currentDate.getMonth() + delta, 1);
        setSearchParams({ month: `${next.getFullYear()}-${String(next.getMonth() + 1).padStart(2, '0')}` });
    };
    const goToPreviousMonth = () => goToMonth(-1);
    const goToNextMonth = () => goToMonth(1);

    // 특정 일기 선택 — 상세 페이지로 이동 (조회는 상세 페이지 몫)
    const handleDiaryClick = (diary) => {
        navigate(`/diary/${diary.diaryId}`);
    };

    // 달력 렌더링 로직
    const renderCalendar = () => {
        const year = currentDate.getFullYear();
        const month = currentDate.getMonth();

        // 해당 월의 첫 번째 날과 마지막 날
        const firstDay = new Date(year, month, 1);
        const lastDay = new Date(year, month + 1, 0);

        // 달력 시작 날짜 (첫 주의 일요일)
        const startDate = new Date(firstDay);
        startDate.setDate(startDate.getDate() - firstDay.getDay());

        // 달력 종료 날짜 (마지막 주의 토요일)
        const endDate = new Date(lastDay);
        endDate.setDate(endDate.getDate() + (6 - lastDay.getDay()));

        const calendarDays = [];
        const currentDay = new Date(startDate);

        while (currentDay <= endDate) {
            // ✅ UTC 대신 로컬 시간 기준으로 날짜 키 생성
            const dateKey = formatLocalDate(currentDay);
            const isCurrentMonth = currentDay.getMonth() === month;
            const diary = diaries[dateKey];
            const isToday = currentDay.toDateString() === new Date().toDateString();

            calendarDays.push({
                date: new Date(currentDay),
                dateKey,
                isCurrentMonth,
                diary,
                isToday
            });

            currentDay.setDate(currentDay.getDate() + 1);
        }

        return calendarDays;
    };

    const calendarDays = renderCalendar();
    const monthNames = [
        '1월', '2월', '3월', '4월', '5월', '6월',
        '7월', '8월', '9월', '10월', '11월', '12월'
    ];
    const dayNames = ['일', '월', '화', '수', '목', '금', '토'];

    return (
        <div className="diary-calendar-page">
            <AppHeader />
            <div className="diary-calendar-panel">
                <div className="calendar-header">
                    <h2>일기 달력</h2>
                    <button onClick={() => navigate('/home')} className="calendar-close-btn">✕</button>
                </div>

                <div className="calendar-content">
                    {error && (
                        <div className="error-message">
                            <div className="error-icon">❌</div>
                            {error}
                        </div>
                    )}

                    <div className="calendar-navigation">
                        <button
                            className="nav-btn"
                            onClick={goToPreviousMonth}
                            disabled={isLoading}
                        >
                            ‹
                        </button>

                        <h3 className="current-month">
                            {currentDate.getFullYear()}년 {monthNames[currentDate.getMonth()]}
                        </h3>

                        <button
                            className="nav-btn"
                            onClick={goToNextMonth}
                            disabled={isLoading}
                        >
                            ›
                        </button>
                    </div>

                    <div className="calendar-grid">
                        {/* 요일 헤더 */}
                        {dayNames.map((day, index) => (
                            <div key={day} className={`day-header ${index === 0 ? 'sunday' : index === 6 ? 'saturday' : ''}`}>
                                {day}
                            </div>
                        ))}

                        {/* 날짜 칸들 */}
                        {calendarDays.map((dayInfo, index) => (
                            <div
                                key={index}
                                className={`calendar-day ${
                                    !dayInfo.isCurrentMonth ? 'other-month' : ''
                                } ${
                                    dayInfo.isToday ? 'today' : ''
                                } ${
                                    dayInfo.diary ? 'has-diary' : ''
                                } ${
                                    index % 7 === 0 ? 'sunday' : index % 7 === 6 ? 'saturday' : ''
                                }`}
                            >
                                <div className="day-number">
                                    {dayInfo.date.getDate()}
                                </div>

                                {dayInfo.diary && (
                                    <div
                                        className="diary-title"
                                        onClick={() => handleDiaryClick(dayInfo.diary)}
                                        title={dayInfo.diary.title}
                                    >
                                        {dayInfo.diary.title}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {isLoading && (
                        <div className="calendar-loading">
                            <div className="loading-spinner"></div>
                            <p>일기를 불러오는 중...</p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default DiaryCalendar;
